import { useEffect, useRef, useState } from "react";
import { PlusIcon, MinusIcon } from "lucide-react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";

export function RegimeAddButton({
  added,
  busy,
  disabled,
  onAdd,
  onRemove,
}: {
  added: boolean;
  busy: boolean;
  disabled: boolean;
  onAdd: (button: HTMLButtonElement) => void;
  onRemove: (button: HTMLButtonElement) => void;
}) {
  const previousAdded = useRef(added);
  const [phase, setPhase] = useState<"idle" | "confirming" | "adopted">(added ? "adopted" : "idle");
  useEffect(() => {
    if (previousAdded.current === added) return;
    previousAdded.current = added;
    if (!added) {
      setPhase("idle");
      return;
    }
    setPhase("confirming");
    const timeout = window.setTimeout(() => setPhase("adopted"), 950);
    return () => window.clearTimeout(timeout);
  }, [added]);
  const state = added ? phase : "idle";
  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      disabled={disabled || busy || state === "confirming"}
      aria-disabled={disabled || busy || state === "confirming"}
      aria-label={added && !busy && state === "adopted" ? "Remove Regime" : undefined}
      aria-busy={busy}
      data-added={added}
      data-state={state}
      onClick={(event) => {
        if (disabled || busy || state === "confirming") return;
        if (added) onRemove(event.currentTarget);
        else onAdd(event.currentTarget);
      }}
      className="regime-add-button relative z-10 min-w-0 flex-1 cursor-pointer disabled:cursor-default"
    >
      <span aria-hidden="true" />
      <Badge variant="success" className="regime-add-label gap-1.5">
        <span aria-hidden="true" className="relative size-3.5 shrink-0">
          <PlusIcon className="regime-add-plus absolute inset-0 size-3.5" />
          <MinusIcon className="regime-remove-icon absolute inset-0 size-3.5" />
          <svg
            className="regime-added-icon absolute inset-0 size-3.5"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path className="regime-add-check" pathLength="1" d="m5 12 4 4L19 6" />
          </svg>
        </span>
        <span aria-live="polite" className="regime-action-labels">
          <span className="regime-status-text">
            <span>
              {busy
                ? added
                  ? "Removing…"
                  : "Adding…"
                : state === "adopted"
                  ? "Adopted"
                  : state === "confirming"
                    ? "Added"
                    : "Add regime"}
            </span>
          </span>
          <span aria-hidden="true" className="regime-remove-text">
            <span>Remove Regime</span>
          </span>
        </span>
      </Badge>
      <span aria-hidden="true" />
    </Button>
  );
}
